import { countdownText, type Recommendation } from '@whaletail/signal-engine';
import type { RecentFundedResult } from '../../lib/btc15History';
import {
  formatPercent,
  formatSignedPercent,
  formatSignedUsd,
  formatUsd,
} from '../../lib/format';
import styles from './Btc15Decision.module.css';

const ACTION_CLASS: Record<Recommendation, string> = {
  UP: styles.actionUp,
  DOWN: styles.actionDown,
  SKIP: styles.actionSkip,
};

const STRENGTH_LABEL: Record<'NO CLEAN EDGE' | 'STRONG' | 'PLAY', string> = {
  'NO CLEAN EDGE': 'No clean edge',
  STRONG: 'Strong model signal (paper)',
  PLAY: 'Model would enter (paper)',
};

/** The live model's settled paper record, shown beside the call so the call never appears without its evidence. */
export interface SettledRecord {
  rate: number | null;
  total: number;
  pnl: number;
  /** The coin-flip baseline's hit rate over its own settled rounds. */
  baselineRate: number | null;
  baselineTotal: number;
}

function recordLine(r: SettledRecord): string {
  if (r.total === 0) return 'Model record: no settled calls yet.';
  const parts = [
    `Model record: ${formatPercent(r.rate ?? undefined, 0)} right over ${r.total} settled ${r.total === 1 ? 'call' : 'calls'}`,
  ];
  if (r.baselineTotal > 0) {
    parts.push(`coin flip ${formatPercent(r.baselineRate ?? undefined, 0)}`);
  }
  parts.push(`${formatSignedUsd(r.pnl)} paper P&L after fees`);
  return `${parts.join(' · ')}.`;
}

const ACTION_ARROW: Record<Recommendation, string> = {
  UP: '↗',
  DOWN: '↘',
  SKIP: '—',
};

export function Btc15Decision({
  recommendation,
  strength,
  probability,
  secondsRemaining,
  roundOpenPrice,
  currentPrice,
  distancePct,
  recentResults,
  record,
  simulatedStake,
  noBuyReason,
  skipReason,
  leaning,
  showPrices = true,
}: {
  recommendation: Recommendation;
  strength: 'NO CLEAN EDGE' | 'STRONG' | 'PLAY';
  probability: number;
  secondsRemaining: number | null;
  roundOpenPrice: number;
  currentPrice: number;
  distancePct: number;
  recentResults?: RecentFundedResult[];
  record?: SettledRecord;
  /** The Market & sizing card's simulated buy for this call, in dollars. */
  simulatedStake?: number;
  /** Why that buy is $0 (e.g. "Edge below 3% minimum"), shown with the call. */
  noBuyReason?: string;
  /** First reason the model skipped this round, shown with a SKIP call. */
  skipReason?: string;
  /** The side the model's score points to, shown small beside a SKIP. */
  leaning?: 'UP' | 'DOWN';
  /** The round-open/current/distance row; the hero hides it while the live
      chart (which shows the same prices) is available. */
  showPrices?: boolean;
}) {
  const urgent = secondsRemaining !== null && secondsRemaining <= 30;
  // The direction comes from the model alone, but whether it's "entered"
  // depends on the live ask: at a price that leaves too little edge the
  // simulated buy is $0, so the call must not read as an entry.
  const unfunded =
    recommendation !== 'SKIP' && simulatedStake !== undefined && simulatedStake <= 0;

  return (
    <section className={styles.call} aria-labelledby="btc15-decision-heading">
      <h2 id="btc15-decision-heading" className="visually-hidden">
        Current model call
      </h2>
      <div className={styles.topRow}>
        <p
          className={`${styles.action} ${unfunded ? styles.actionSkip : ACTION_CLASS[recommendation]}`}
        >
          {ACTION_ARROW[recommendation]} {recommendation}
        </p>
        {recommendation === 'SKIP' ? (
          // A skip is "no bet". The model's number stays available but
          // small, so it can't read as confidence in a bet nobody is making.
          <p className={styles.leaning}>
            {leaning ? `Model leans ${leaning}` : 'Model score'},{' '}
            {(probability * 100).toFixed(0)}%
          </p>
        ) : (
          <div className={styles.confidence}>
            <span className={styles.confidenceValue}>
              {(probability * 100).toFixed(1)}%
            </span>
            <span className={styles.confidenceLabel}>Model probability</span>
          </div>
        )}
      </div>

      <div className={styles.metaRow}>
        <span className={unfunded ? `${styles.pill} ${styles.pillMuted}` : styles.pill}>
          {unfunded ? 'No buy at this price' : (STRENGTH_LABEL[strength] ?? strength)}
        </span>
        <span
          className={`${styles.countdown} ${urgent ? styles.countdownUrgent : ''}`}
          role="timer"
          aria-live={urgent ? 'assertive' : 'off'}
        >
          {secondsRemaining !== null ? `${countdownText(secondsRemaining)} left` : '—'}
        </span>
      </div>

      {showPrices && (
        <div className={styles.priceRow}>
          <div className={styles.priceMetric}>
            <span className={styles.priceLabel}>Round open</span>
            <span className={styles.priceValue}>{formatUsd(roundOpenPrice)}</span>
          </div>
          <div className={styles.priceMetric}>
            <span className={styles.priceLabel}>Current price</span>
            <span className={styles.priceValue}>{formatUsd(currentPrice)}</span>
          </div>
          <div className={styles.priceMetric}>
            <span className={styles.priceLabel}>Distance</span>
            <span
              className={`${styles.priceValue} ${
                distancePct >= 0 ? styles.distancePositive : styles.distanceNegative
              }`}
            >
              {formatSignedPercent(distancePct)}
            </span>
          </div>
        </div>
      )}
      {skipReason && recommendation === 'SKIP' && (
        <p className={styles.noBuy}>No call this round: {skipReason.toLowerCase()}.</p>
      )}
      {unfunded && noBuyReason && (
        <p className={styles.noBuy}>
          No simulated buy this round: {noBuyReason.toLowerCase()}.
        </p>
      )}
      {(record || (recentResults && recentResults.length > 0)) && (
        <div className={styles.recordRow}>
          {record && <p className={styles.record}>{recordLine(record)}</p>}
          {recentResults && recentResults.length > 0 && (
            <span className={styles.recent}>
              <span className={styles.recentLabel} aria-hidden="true">
                Last {recentResults.length}
              </span>
              <span
                className={styles.recentChips}
                aria-label={`Last ${recentResults.length} simulated buys, oldest first: ${[
                  ...recentResults,
                ]
                  .reverse()
                  .map((r) => (r.won ? 'won' : 'lost'))
                  .join(', ')}`}
                role="img"
              >
                {[...recentResults].reverse().map((r) => (
                  <span
                    key={r.roundId}
                    className={r.won ? styles.recentWin : styles.recentLoss}
                    title={`${r.action} — ${r.won ? 'won' : 'lost'}`}
                  >
                    {r.won ? 'W' : 'L'}
                  </span>
                ))}
              </span>
            </span>
          )}
        </div>
      )}
    </section>
  );
}
